import type { ImportedLenderUser, Lender } from '../../api/lenders';
import { userSchema } from './wizard/schemas/user.schema';
import { USER_ROLE_LABELS, type UserRole } from './wizard/types';

type Field = keyof ImportedLenderUser;

export type ImportColumn = { header: string; field: Field; width: number };

/** One user read from the file, with the sheet row it came from (the header is row 1). */
export type ImportedRow = { row: number; user: ImportedLenderUser };

export type ImportFileResult = { users: ImportedRow[]; errors: string[] };

const REQUIRED: Field[] = ['firstName', 'lastName', 'email'];
const DEFAULT_ROLE: UserRole = 'lender-standard';
const ROLE_NAMES = Object.values(USER_ROLE_LABELS);
const ROLE_HINT = `${ROLE_NAMES.slice(0, -1).join(', ')} or ${ROLE_NAMES[ROLE_NAMES.length - 1]}`;

const UNSUPPORTED = 'Choose an Excel (.xlsx) or CSV file.';
const UNREADABLE = 'The file could not be read. Check it is a valid Excel or CSV file.';
const NO_USERS = 'The file has no users.';

/** The template's columns: the MeridianLink user names only for the integrations the lender has. */
export const importColumns = (lender: Lender): ImportColumn[] => [
  { header: 'First Name', field: 'firstName', width: 18 },
  { header: 'Last Name', field: 'lastName', width: 18 },
  { header: 'Email', field: 'email', width: 32 },
  { header: 'Role', field: 'role', width: 18 },
  ...(lender.integrations?.meridianlink
    ? [{ header: 'MeridianLink Consumer Username', field: 'mlConsumerUserName' as Field, width: 32 }]
    : []),
  ...(lender.integrations?.['meridianlink-mortgage']
    ? [{ header: 'MeridianLink Mortgage Username', field: 'mlMortgageUserName' as Field, width: 32 }]
    : []),
];

/**
 * The header row only: an example row would be imported if the admin forgot to delete it. The
 * spreadsheet writer loads on demand, as for the export.
 */
export const buildImportTemplate = async (lender: Lender) => {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const columns = importColumns(lender);
  return writeXlsxFile([columns.map((column) => ({ value: column.header, fontWeight: 'bold' as const }))], {
    sheet: 'Users',
    columns: columns.map((column) => ({ width: column.width })),
    stickyRowsCount: 1,
  });
};

export const downloadImportTemplate = async (lender: Lender) =>
  (await buildImportTemplate(lender)).toFile(`${lender.name}-users-import-template.xlsx`);

const normalize = (value: unknown) =>
  String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const cellText = (value: unknown) => (value instanceof Date ? value.toISOString() : String(value ?? '').trim());

const roleOf = (value: string): UserRole | undefined => {
  if (!value) return DEFAULT_ROLE;
  const wanted = normalize(value).replace(/-/g, ' ');
  return (Object.keys(USER_ROLE_LABELS) as UserRole[]).find(
    (role) => normalize(USER_ROLE_LABELS[role]).replace(/-/g, ' ') === wanted || role.replace(/-/g, ' ') === wanted,
  );
};

const join = (items: string[]) =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/** The rows of a sheet, header first, into users — or every problem found, row by row. */
export function parseImportRows(rows: unknown[][], lender: Lender): ImportFileResult {
  const columns = importColumns(lender);
  const header = (rows[0] ?? []).map(normalize);
  const indexOf = new Map(
    columns.map((column) => [column.field, header.indexOf(normalize(column.header))] as const),
  );

  const missing = columns.filter((column) => REQUIRED.includes(column.field) && indexOf.get(column.field) === -1);
  if (missing.length > 0) {
    return {
      users: [],
      errors: [`The file is missing the ${join(missing.map((column) => column.header))} ${missing.length === 1 ? 'column' : 'columns'}. Use the template.`],
    };
  }

  const users: ImportedRow[] = [];
  const errors: string[] = [];
  const rowOfEmail = new Map<string, number>();

  rows.slice(1).forEach((cells, index) => {
    const row = index + 2;
    const value = (field: Field) => {
      const at = indexOf.get(field) ?? -1;
      return at === -1 ? '' : cellText(cells[at]);
    };
    if (columns.every((column) => value(column.field) === '')) return;

    const firstName = value('firstName');
    const lastName = value('lastName');
    const email = value('email');
    const roleText = value('role');
    const role = roleOf(roleText);

    const problems: string[] = [];
    const checked = userSchema.safeParse({
      firstName,
      lastName,
      email,
      role: role ?? DEFAULT_ROLE,
      mlConsumerUserName: '',
      mlMortgageUserName: '',
    });
    const invalid = new Set(checked.success ? [] : checked.error.issues.map((issue) => issue.path[0]));
    if (invalid.has('firstName')) problems.push(firstName ? 'First Name is not valid' : 'First Name is missing');
    if (invalid.has('lastName')) problems.push(lastName ? 'Last Name is not valid' : 'Last Name is missing');
    if (invalid.has('email')) problems.push(email ? `"${email}" is not a valid email` : 'Email is missing');
    if (!role) problems.push(`Role "${roleText}" is not valid. Use ${ROLE_HINT}`);

    const key = email.toLowerCase();
    const first = rowOfEmail.get(key);
    if (email && !invalid.has('email') && first !== undefined) problems.push(`${email} is already in row ${first}`);
    if (email && first === undefined) rowOfEmail.set(key, row);

    if (problems.length > 0) {
      errors.push(...problems.map((problem) => `Row ${row}: ${problem}`));
      return;
    }

    const user: ImportedLenderUser = { firstName, lastName, email, role: role ?? DEFAULT_ROLE };
    const mlConsumerUserName = value('mlConsumerUserName');
    const mlMortgageUserName = value('mlMortgageUserName');
    if (mlConsumerUserName) user.mlConsumerUserName = mlConsumerUserName;
    if (mlMortgageUserName) user.mlMortgageUserName = mlMortgageUserName;
    users.push({ row, user });
  });

  if (users.length === 0 && errors.length === 0) errors.push(NO_USERS);
  return { users, errors };
}

/** Excel's "CSV UTF-8" starts with a byte order mark, which would stick to the first header. */
const withoutBom = (text: string) => (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text);

/** Reads an Excel (.xlsx) or CSV file into users. The parsers load only when a file is read. */
export async function readImportFile(file: File, lender: Lender): Promise<ImportFileResult> {
  const extension = file.name.toLowerCase().split('.').pop();
  if (extension !== 'xlsx' && extension !== 'csv') return { users: [], errors: [UNSUPPORTED] };

  let rows: unknown[][];
  try {
    if (extension === 'xlsx') {
      const { readSheet } = await import('read-excel-file/universal');
      rows = await readSheet(file);
    } else {
      const { default: Papa } = await import('papaparse');
      // Empty lines are kept so the row numbers match what the admin sees in the sheet.
      rows = Papa.parse<string[]>(withoutBom(await file.text())).data;
    }
  } catch {
    return { users: [], errors: [UNREADABLE] };
  }
  return parseImportRows(rows, lender);
}
